/* eslint-disable */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "../supabase";
import { supabaseWithRetry } from "../utils";
import { DEFAULT_OVERDUE_RULES, DEFAULT_DEV_FIELDS, DEFAULT_FIELDS, DEFAULT_THEME } from "../shared";
import { applyFilters, applySortOrder, hasActiveFilters } from "./useCatalogFilters";
import { useQueryClient } from "@tanstack/react-query";
import { TABS_READING_ALL_ITEMS, TABS_READING_LINKS, readItemsStamp } from "./appShellLoads";
import { useItemsStatsSlim, useRefreshItemsStats } from "./useItemsStatsSlim"; import { useDevLinksSlim } from "./useDevLinksSlim";
import { useAppDevs } from "./useAppDevs"; import { useConfigPoll } from "./useConfigPoll";   // AUD-05: split out (300-line cap)

const PAGE_SIZE = 60;
const SCROLL_FILTER_KEYS = ["brand", "gender", "type", "material", "status", "shape", "assigned", "createdBy", "search", "sortBy"];

export function useAppData({ user, filtersRef, onShowToast }) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const [totalCount, setTotalCount] = useState(0);
  const [wanted, setWanted] = useState({ items: false, links: false });   // the slim app-wide reads a screen has asked for (loadDevsIfNeeded)
  const allItemsStats = useItemsStatsSlim(wanted.items); const linksSlim = useDevLinksSlim(wanted.links); const refreshAllItemsStats = useRefreshItemsStats();
  const [pageLoading, setPageLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { devs, setDevs, reloadDevs, loadDevsFor } = useAppDevs(queryClient);
  const [devSettings, setDevSettings] = useState({ overdueRules: { ...DEFAULT_OVERDUE_RULES }, devFields: [...DEFAULT_DEV_FIELDS] });
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [catalogViewSize, setCatalogViewSize] = useState(null); // null = pending from system_config
  const [devRequests, setDevRequests] = useState([]);
  const [devRequestsLoaded, setDevRequestsLoaded] = useState(false);
  const [activityLog, setActivityLog] = useState([]);

  const pollRef = useRef(null);
  const pollStampRef = useRef(null);    // "count|newest created_at" — the poll re-reads rows only when it moves
  const _forceRefreshSeenRef = useConfigPoll({ setTheme, setFields });   // system_config re-read: 5 min, visible tab only
  const fieldsLoadedFromDB = useRef(false);
  const brandSyncRef = useRef(false);

  useEffect(() => { itemsRef.current = items; }, [items]);

  const showToast = onShowToast || (() => {});

  // Fetch items — paged or all when filtered
  const fetchPagedItems = useCallback(async (offset = 0, append = false, overrideFilters) => {
    setPageLoading(true);
    try {
      const f = overrideFilters || filtersRef.current;
      const isFiltered = hasActiveFilters(f);
      if (isFiltered && offset === 0 && !append) {
        let all = [], from = 0, total = 0;
        const BATCH = 1000;
        while (true) {
          const { data, count, error } = await supabaseWithRetry(() => {
            let q = supabase.from("items").select("*", { count: "exact" });
            q = applySortOrder(q, f.sortBy || "newest");
            q = applyFilters(q, f);
            return q.range(from, from + BATCH - 1);
          });
          if (error || !data || data.length === 0) { if (from === 0) total = count ?? 0; break; }
          if (from === 0) total = count ?? 0;
          all = [...all, ...data];
          if (data.length < BATCH || all.length >= total) break;
          from += BATCH;
        }
        setItems(all.map(row => ({ ...row.data, id: row.id })));
        setTotalCount(total);
      } else {
        const { data, count, error } = await supabaseWithRetry(() => {
          let q = supabase.from("items").select("*", { count: "exact" });
          q = applySortOrder(q, f.sortBy || "newest");
          q = applyFilters(q, f);
          return q.range(offset, offset + PAGE_SIZE - 1);
        });
        if (!error && data) {
          const mapped = data.map(row => ({ ...row.data, id: row.id }));
          if (append) setItems(prev => [...prev, ...mapped]);
          else setItems(mapped);
          setTotalCount(count ?? 0);
        }
      }
    } catch (e) { console.error("fetchPagedItems error:", e); }
    setPageLoading(false);
  }, []);

  // Fetch ALL items for stats/export
  const fetchAllItemsRaw = useCallback(async (onProgress) => {
    let all = [], from = 0;
    while (true) {
      const { data, error } = await supabaseWithRetry(() =>
        supabase.from("items").select("*").order("created_at", { ascending: false }).range(from, from + 999)
      );
      if (error) { console.error('fetchAllItemsRaw error:', error); showToast('Failed to load items: ' + error.message, true); break; }
      if (!data || !data.length) break;
      all = [...all, ...data];
      if (onProgress) onProgress(all.length);
      if (data.length < 1000) break;
      from += 1000;
    }
    return all.map(row => ({ ...row.data, id: row.id }));
  }, []);

  const loadMoreItems = useCallback(() => {
    fetchPagedItems(items.length, true);
  }, [items.length, fetchPagedItems]);

  // The full developments list (fetch, reloadDevs, when it starts) lives in useAppDevs.js.

  // ── INITIAL LOAD + POLLING ──
  const lsGet = key => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch(e) { return null; } };

  useEffect(() => {
    let active = true, pollVersion = 0;
    // Notification history is optional; it must not hold the workspace splash open.
    Promise.resolve(supabase.from("dev_requests").select("*").order("created_at", { ascending: false }))
      .then(({ data, error }) => {
        if (!active || error || !data) return;
        setDevRequests(prev => {
          const seen = new Set();
          return [...prev, ...data].filter(request => {
            if (seen.has(request.id)) return false;
            seen.add(request.id); return true;
          });
        });
      }).catch(() => {}) // Optional history failure leaves the workspace usable.
      .finally(() => { if (active) setDevRequestsLoaded(true); });
    Promise.all([
      (async () => {
        let q = supabase.from("items").select("*", { count: "exact" }).order("created_at", { ascending: false }).range(0, PAGE_SIZE - 1);
        return q;
      })(),
      supabase.from("system_config").select("key,value").in("key", ["theme", "fields", "dev_settings", "catalog_view_size", "force_refresh"]),
      Promise.resolve({ data: [] }),
    ]).then(async ([itemsPageRes, configRes, devsRes]) => {
      if (configRes.data) {
        configRes.data.forEach(row => {
          if (row.key === "theme") setTheme(row.value);
          if (row.key === "fields" && !window.__clb_fields_saving) { setFields(row.value); fieldsLoadedFromDB.current = true; }
          if (row.key === "dev_settings" && row.value?.devFields?.length > 0) {
            setDevSettings({ overdueRules: row.value.overdueRules || { ...DEFAULT_OVERDUE_RULES }, devFields: row.value.devFields });
          }
          if (row.key === "catalog_view_size" && row.value !== null && row.value !== undefined) setCatalogViewSize(Number(row.value));
          if (row.key === "force_refresh" && row.value?.ts && _forceRefreshSeenRef.current === null) _forceRefreshSeenRef.current = row.value.ts;   // the baseline (useConfigPoll)
        });
      }
      if (itemsPageRes.error) {
        const ir = lsGet("clb_items_v1"); setItems(ir || []);
      } else if (!itemsPageRes.data || itemsPageRes.data.length === 0) {
        const ir = lsGet("clb_items_v1");
        if (ir && ir.length > 0) {
          const rows = ir.map(item => ({ id: String(item.id), data: item, created_at: new Date().toISOString() }));
          await supabase.from("items").insert(rows);
          setItems(ir); setTotalCount(ir.length);
        } else { setItems([]); setTotalCount(0); }
      } else {
        setItems(itemsPageRes.data.map(row => ({ ...row.data, id: row.id })));
        setTotalCount(itemsPageRes.count ?? itemsPageRes.data.length);
      }
      setLoaded(true);
      // App-shell 2026-09-14: neither app-wide list is loaded at boot any more — loadDevsIfNeeded(tab)
      // loads each on the first entry into a screen that reads it (appShellLoads.js).
    });

    // Polling (60s) — items only; the system_config re-read moved to useConfigPoll (AUD-05)
    pollRef.current = setInterval(async () => {
      const version = ++pollVersion;
      const scrollPos = window.scrollY;
      const f = filtersRef.current;
      // Compare user filter inputs; App also replaces the wrapper for derived link maps.
      const canRestore = () => active && version === pollVersion && document.visibilityState !== "hidden"
        && SCROLL_FILTER_KEYS.every(key => filtersRef.current[key] === f[key]) && window.scrollY === scrollPos;
      try {
        const { liveCount, stamp } = await readItemsStamp();                 // no rows unless something moved
        const moved = pollStampRef.current !== null && stamp !== pollStampRef.current;
        pollStampRef.current = stamp;
        const currentCount = itemsRef.current.length;
        const fetchCount = Math.max(PAGE_SIZE, currentCount);
        let q = supabase.from("items").select("*", { count: "exact" });
        q = applySortOrder(q, f.sortBy || "newest");
        q = applyFilters(q, f);
        q = q.range(0, fetchCount - 1);
        const { data, count } = moved ? await q : { data: null, count: liveCount };
        if (!moved && typeof liveCount === "number" && !hasActiveFilters(f)) setTotalCount(liveCount);
        if (data) {
          const fresh = data.map(row => ({ ...row.data, id: row.id }));
          setItems(prev => {
            const freshById = {};
            fresh.forEach(item => { freshById[item.id] = item; });
            const merged = prev.map(item => freshById[item.id] || item).filter(item => freshById[item.id]);
            const existingIds = new Set(merged.map(i => i.id));
            fresh.forEach(item => { if (!existingIds.has(item.id)) merged.push(item); });
            return merged;
          });
          setTotalCount(count ?? 0);
          if (canRestore()) requestAnimationFrame(() => { if (canRestore()) window.scrollTo(0, scrollPos); });
        }
      } catch (e) { console.error("Poll items error:", e); }
    }, 60000);
    return () => { active = false; clearInterval(pollRef.current); };
  }, [fetchAllItemsRaw]);

  // Realtime sync for items — sub-second cross-tab grid update.
  // T-026 Phase 4 Option B legacy bridge. Coexists with the 60s polling
  // above (safety net for dropped websockets / missed reconnect events)
  // and with useCatalogRealtime (handles ['items'] and ['portal-counts']
  // RQ invalidation). Mirrors DevelopmentsTab.js:182 pattern verbatim,
  // including the UPDATE re-fetch to dodge JSONB truncation in Realtime
  // payloads.
  useEffect(() => {
    const channel = supabase.channel("items-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "items" }, async (payload) => {
        if (payload.eventType === "INSERT") {
          const row = payload.new;
          if (!row?.id) return;
          const item = { ...(row.data || {}), id: row.id };
          setItems(p => (p.some(i => i.id === item.id) ? p : [item, ...p]));
          setTotalCount(c => c + 1);
        } else if (payload.eventType === "UPDATE") {
          const id = payload.new?.id;
          if (!id) return;
          const { data: fresh } = await supabase.from("items").select("*").eq("id", id).single();
          if (!fresh) return;
          const item = { ...(fresh.data || {}), id: fresh.id };
          setItems(p => p.map(i => (i.id === item.id ? item : i)));
        } else if (payload.eventType === "DELETE") {
          const id = payload.old?.id;
          if (!id) return;
          setItems(p => p.filter(i => i.id !== id));
          setTotalCount(c => Math.max(0, c - 1));
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // The app-wide lists load on the first entry into a screen that reads them (appShellLoads.js)
  const loadDevsIfNeeded = useCallback((tab) => {
    const w = { items: TABS_READING_ALL_ITEMS.has(tab), links: TABS_READING_LINKS.has(tab) };
    if (w.items || w.links) setTimeout(() => setWanted(p => (p.items || !w.items) && (p.links || !w.links) ? p : { items: p.items || w.items, links: p.links || w.links }), 3000);   // 3 s behind the screen's own first page, as at boot before
    loadDevsFor(tab);   // the full dev list — on Developments' Board, after the Board's first page (devsAfterBoard.js)
  }, [loadDevsFor]);

  // Brand auto-sync
  useEffect(() => {
    if (window.__clb_fields_saving) return;
    if (!fieldsLoadedFromDB.current) return;
    if (window.__clb_fields_last_save && (Date.now() - window.__clb_fields_last_save < 60000)) return;
    const src = allItemsStats.length > 0 ? allItemsStats : items;
    const itemBrandsNotInFields = [...new Set(src.map(i => i.brand).filter(Boolean))]
      .filter(b => !(fields.find(f => f.key === "brand")?.options || []).includes(b));
    if (itemBrandsNotInFields.length > 0 && !brandSyncRef.current) {
      brandSyncRef.current = true;
      supabase.from("system_config").select("value").eq("key", "fields").single()
        .then(({ data: freshData }) => {
          if (window.__clb_fields_saving) { brandSyncRef.current = false; return; }
          const freshFields = freshData?.value || fields;
          const freshBrandField = freshFields.find(f => f.key === "brand");
          if (!freshBrandField) { brandSyncRef.current = false; return; }
          const stillMissing = itemBrandsNotInFields.filter(b => !(freshBrandField.options || []).includes(b));
          if (stillMissing.length === 0) { setFields(freshFields); brandSyncRef.current = false; return; }
          const updated = freshFields.map(f => f.key === "brand" ? { ...f, options: [...new Set([...(f.options || []), ...stillMissing])].sort() } : f);
          setFields(updated);
          supabase.from("system_config").upsert({ key: "fields", value: updated, updated_at: new Date().toISOString() }, { onConflict: 'key' })
            .then(() => { brandSyncRef.current = false; }).catch(() => { brandSyncRef.current = false; });
        }).catch(() => { brandSyncRef.current = false; });
    }
  }, [allItemsStats, items, fields]);

  return {
    items, setItems, totalCount, setTotalCount, allItemsStats, refreshAllItemsStats, linksSlim,
    pageLoading, loaded, devs, setDevs, devSettings, setDevSettings,
    fields, setFields, theme, setTheme, catalogViewSize, setCatalogViewSize,
    devRequests, setDevRequests, devRequestsLoaded, activityLog, setActivityLog,
    fetchPagedItems, fetchAllItemsRaw, loadMoreItems, loadDevsIfNeeded, reloadDevs,
  };
}
